import type {AdminAccessBundle} from './accessRequests'
import type {PermissionCatalogEntry} from './permissionGrouping'
import {resolveCatalogEntry} from './permissionGrouping'

const normalizeCode = (value: string): string => value.trim().toUpperCase()

/** Modules shown in the simple “Feature access” step (matches admin module taxonomy). */
export const SIMPLE_WIZARD_MODULES = [
  {key: 'Admin', label: 'Admin'},
  {key: 'Operations', label: 'Operations'},
  {key: 'Technical', label: 'Technical'},
  {key: 'QHSE', label: 'QHSE'},
  {key: 'Procurement', label: 'Procurement'},
  {key: 'Crewing', label: 'Crewing'},
  {key: 'Vessels', label: 'Vessels'},
  {key: 'Reports', label: 'Reports'},
] as const

export type SimpleWizardModuleKey = (typeof SIMPLE_WIZARD_MODULES)[number]['key']

export type SimpleWizardActionKey = 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'download'

export const SIMPLE_ACTION_LABELS: Record<SimpleWizardActionKey, string> = {
  view: 'View',
  create: 'Create / add',
  edit: 'Edit',
  delete: 'Delete',
  approve: 'Approve / workflow',
  download: 'Download / export',
}

/** Human labels per module row (tooltips / helper copy). */
export const SIMPLE_MODULE_HELP: Partial<Record<SimpleWizardModuleKey, string>> = {
  Admin: 'User and company administration, roles, access setup',
  Operations: 'Voyages, cargo, bunker, operations reporting',
  Technical: 'PMS, machinery, equipment, running hours, technical libraries',
  QHSE: 'Safety, certificates, audits, QHSE forms and records',
  Procurement: 'Requisitions, RFQs, quotations, purchase orders, GRN, invoices',
  Crewing: 'Crew, manning, contracts, rest hours',
  Vessels: 'Fleet and vessel records',
  Reports: 'Reporting and exports that are not tied to a single module above',
}

export type AccessTemplateId =
  | 'basic_user'
  | 'company_admin'
  | 'vessel_crew'
  | 'operations'
  | 'technical'
  | 'qhse'
  | 'procurement'
  | 'read_only'
  | 'custom'
  | 'blank'

export type AccessTemplateDef = {
  id: AccessTemplateId
  title: string
  description: string
  /** Maps to server `base_role_type` for new custom roles. */
  baseRoleType: string
  defaultDataScope: 'ALL' | 'COMPANY' | 'VESSEL'
  /** Prefer packages whose code contains one of these substrings (case-insensitive). */
  bundleCodeHints: string[]
  /** Initial feature toggles (merged with all-false base). */
  initialFeatures: Partial<Record<SimpleWizardModuleKey, Partial<Record<SimpleWizardActionKey, boolean>>>>
}

export const ACCESS_TEMPLATES: AccessTemplateDef[] = [
  {
    id: 'basic_user',
    title: 'Basic user',
    description: 'View assigned dashboards and everyday records; limited changes.',
    baseRoleType: 'SHORE',
    defaultDataScope: 'COMPANY',
    bundleCodeHints: ['BASIC', 'READ', 'DEFAULT', 'USER'],
    initialFeatures: {
      Operations: {view: true},
      Vessels: {view: true},
      Reports: {view: true},
    },
  },
  {
    id: 'company_admin',
    title: 'Company admin',
    description: 'Manage people and company-level data (typical shore administrator).',
    baseRoleType: 'COMPANY_ADMIN',
    defaultDataScope: 'COMPANY',
    bundleCodeHints: ['ADMIN', 'COMPANY', 'MANAGEMENT'],
    initialFeatures: {
      Admin: {view: true, create: true, edit: true, delete: true, approve: true, download: true},
      Operations: {view: true, create: true, edit: true, delete: true, approve: true, download: true},
      Technical: {view: true, create: true, edit: true, delete: true, download: true},
      QHSE: {view: true, create: true, edit: true, delete: true, approve: true, download: true},
      Procurement: {view: true, create: true, edit: true, approve: true, download: true},
      Crewing: {view: true, create: true, edit: true, download: true},
      Vessels: {view: true, create: true, edit: true, download: true},
      Reports: {view: true, download: true},
    },
  },
  {
    id: 'vessel_crew',
    title: 'Vessel user / crew',
    description: 'Vessel-scoped access for sea staff and vessel workflows.',
    baseRoleType: 'CREW',
    defaultDataScope: 'VESSEL',
    bundleCodeHints: ['CREW', 'VESSEL', 'OPERATIONS'],
    initialFeatures: {
      Operations: {view: true, create: true, edit: true, download: true},
      Technical: {view: true, create: true, edit: true, download: true},
      QHSE: {view: true, create: true, edit: true, download: true},
      Procurement: {view: true, create: true, edit: true},
      Crewing: {view: true, edit: true, download: true},
      Vessels: {view: true, edit: true},
      Reports: {view: true, download: true},
    },
  },
  {
    id: 'operations',
    title: 'Operations user',
    description: 'Voyages, cargo, daily reports, and operational data.',
    baseRoleType: 'SHORE',
    defaultDataScope: 'COMPANY',
    bundleCodeHints: ['OPERATION', 'VOYAGE'],
    initialFeatures: {
      Operations: {view: true, create: true, edit: true, delete: true, approve: true, download: true},
      Vessels: {view: true, edit: true, download: true},
      Reports: {view: true, download: true},
    },
  },
  {
    id: 'technical',
    title: 'Technical user',
    description: 'PMS, machinery, equipment, running hours, and technical libraries.',
    baseRoleType: 'SHORE',
    defaultDataScope: 'COMPANY',
    bundleCodeHints: ['TECH', 'PMS'],
    initialFeatures: {
      Technical: {view: true, create: true, edit: true, delete: true, download: true},
      Operations: {view: true, download: true},
      Reports: {view: true, download: true},
    },
  },
  {
    id: 'qhse',
    title: 'QHSE user',
    description: 'Safety, certificates, audits, forms, and QHSE workflows.',
    baseRoleType: 'SHORE',
    defaultDataScope: 'COMPANY',
    bundleCodeHints: ['QHSE', 'HSE', 'SAFETY'],
    initialFeatures: {
      QHSE: {view: true, create: true, edit: true, delete: true, approve: true, download: true},
      Reports: {view: true, download: true},
    },
  },
  {
    id: 'procurement',
    title: 'Procurement user',
    description: 'Requisitions, RFQs, quotations, POs, goods receipt, and invoices.',
    baseRoleType: 'SHORE',
    defaultDataScope: 'COMPANY',
    bundleCodeHints: ['PROCURE', 'PROCUREMENT'],
    initialFeatures: {
      Procurement: {view: true, create: true, edit: true, delete: true, approve: true, download: true},
      Reports: {view: true, download: true},
    },
  },
  {
    id: 'read_only',
    title: 'Read only',
    description: 'View-only access across common areas (no create/edit/delete).',
    baseRoleType: 'SHORE',
    defaultDataScope: 'COMPANY',
    bundleCodeHints: ['READ', 'VIEW'],
    initialFeatures: {
      Admin: {view: true},
      Operations: {view: true},
      Technical: {view: true},
      QHSE: {view: true},
      Procurement: {view: true},
      Crewing: {view: true},
      Vessels: {view: true},
      Reports: {view: true},
    },
  },
  {
    id: 'custom',
    title: 'Custom',
    description: 'Start from a blank feature checklist and adjust toggles yourself.',
    baseRoleType: 'COMPANY_ADMIN',
    defaultDataScope: 'COMPANY',
    bundleCodeHints: [],
    initialFeatures: {},
  },
  {
    id: 'blank',
    title: 'Blank role',
    description: 'No permissions yet — configure later with feature access or fine-tuning.',
    baseRoleType: 'COMPANY_ADMIN',
    defaultDataScope: 'COMPANY',
    bundleCodeHints: [],
    initialFeatures: {},
  },
]

export function getAccessTemplate(id: AccessTemplateId): AccessTemplateDef | undefined {
  return ACCESS_TEMPLATES.find((t) => t.id === id)
}

export function emptyFeatureState(): Record<SimpleWizardModuleKey, Record<SimpleWizardActionKey, boolean>> {
  const out = {} as Record<SimpleWizardModuleKey, Record<SimpleWizardActionKey, boolean>>
  for (const row of SIMPLE_WIZARD_MODULES) {
    const m = row.key
    out[m] = {
      view: false,
      create: false,
      edit: false,
      delete: false,
      approve: false,
      download: false,
    }
  }
  return out
}

export function mergeTemplateIntoFeatures(
  template: AccessTemplateDef | undefined
): Record<SimpleWizardModuleKey, Record<SimpleWizardActionKey, boolean>> {
  const base = emptyFeatureState()
  if (!template) return base
  const init = template.initialFeatures
  for (const mk of Object.keys(init) as SimpleWizardModuleKey[]) {
    if (!base[mk]) continue
    const row = init[mk]
    if (!row) continue
    for (const ak of Object.keys(row) as SimpleWizardActionKey[]) {
      if (row[ak]) base[mk][ak] = true
    }
  }
  return base
}

function codeMatchesAction(code: string, action: SimpleWizardActionKey): boolean {
  const u = normalizeCode(code)
  switch (action) {
    case 'view':
      return (
        /_VIEW$|_READ$|_SHOW$|_LIST$/.test(u) ||
        u.startsWith('READ_') ||
        u.includes('_VIEW_')
      )
    case 'create':
      return (
        /_CREATE$|_ADD$|_INSERT$|_NEW$/.test(u) || u.startsWith('CREATE_') || u.includes('_CREATE_')
      )
    case 'edit':
      return /_EDIT$|_UPDATE$|_MODIFY$/.test(u) || u.startsWith('UPDATE_') || u.startsWith('EDIT_')
    case 'delete':
      return /_DELETE$|_REMOVE$/.test(u) || u.startsWith('DELETE_')
    case 'approve':
      return /APPROVE|APPROVAL/.test(u)
    case 'download':
      return /DOWNLOAD|EXPORT|_EXPORT$/.test(u)
    default:
      return false
  }
}

/**
 * Collect permission codes from the live catalog matching module + enabled actions.
 * Uses resolved moduleCategory only — same source of truth as the rest of admin access.
 */
export function collectCatalogCodesForFeatures(
  catalog: PermissionCatalogEntry[],
  features: Record<SimpleWizardModuleKey, Record<SimpleWizardActionKey, boolean>>
): string[] {
  const set = new Set<string>()
  for (const raw of catalog) {
    const e = resolveCatalogEntry(raw)
    const mod = (e.moduleCategory || '').trim() as SimpleWizardModuleKey | string
    if (!mod || !(mod in features)) continue
    const row = features[mod as SimpleWizardModuleKey]
    const code = normalizeCode(e.permissionCode)
    for (const action of Object.keys(row) as SimpleWizardActionKey[]) {
      if (!row[action]) continue
      if (codeMatchesAction(code, action)) set.add(code)
    }
  }
  return Array.from(set)
}

export function collectBundlePermissionCodes(bundle: AdminAccessBundle | null | undefined): string[] {
  if (!bundle?.permissions?.length) return []
  return bundle.permissions
    .filter((p) => (p.effect || 'ALLOW') === 'ALLOW')
    .map((p) => normalizeCode(p.permissionCode))
    .filter(Boolean)
}

/** Pick up to N suggested packages for a template (non-destructive UI hint). */
export function suggestBundlesForTemplate(
  bundles: AdminAccessBundle[],
  template: AccessTemplateDef | undefined,
  maxPick = 3
): AdminAccessBundle[] {
  if (!template?.bundleCodeHints?.length) return []
  const hints = template.bundleCodeHints.map((h) => h.toUpperCase())
  const scored = bundles
    .filter((b) => b.active)
    .map((b) => {
      const c = (b.code || '').toUpperCase()
      let score = 0
      for (const h of hints) {
        if (c.includes(h)) score += 2
        if ((b.name || '').toUpperCase().includes(h)) score += 1
      }
      return {b, score}
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
  return scored.slice(0, maxPick).map((x) => x.b)
}

export function roleScopeLabel(
  r: {customRole?: boolean; companyGroupAdminId?: number | null},
  isPlatformSuperAdminViewer: boolean
): {type: string; detail: string} {
  if (!r.customRole) {
    return {type: 'System', detail: 'Built-in role type'}
  }
  if (r.companyGroupAdminId == null || r.companyGroupAdminId === undefined) {
    return {type: 'Global', detail: 'Available across companies (platform)'}
  }
  return {
    type: 'Company-specific',
    detail: isPlatformSuperAdminViewer
      ? `Company group #${r.companyGroupAdminId}`
      : 'Your organization',
  }
}
